import { AddCircle, Edit, RecordVoiceOver } from '@mui/icons-material'
import { inject, observer } from 'mobx-react'
import * as React from 'react'
import { IPatient } from '../../Definitions'
import { IStores } from '../../Stores'
import {
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import ReactPaginate from 'react-paginate'
import { pagedList } from 'src/viewModels/pagedList'
import { ReferralSummaryResult } from 'src/viewModels/ReferralSummaryResult'
import { SearchRequestBase } from 'src/viewModels/SearchRequestBase'
import { searchReferrals } from 'src/services/PatientService'
import moment from 'moment'

interface IReferralsProps {
  editReferral?: (referralId: string | undefined, patientId: string) => void
  loadEpisodesOfCare?: () => Promise<void>
  selectedPatient?: IPatient
  setSelectedReferral?: (referralId: string) => Promise<void>
  setPage?: (page: number) => void
}

class Referrals extends React.Component<IReferralsProps> {
  public state = {
    gridLoading: false,
    gridData: new pagedList().items as ReferralSummaryResult[],
    page: 1,
    pageCount: 0,
    firstRecordIndex: 0,
    lastRecordIndex: 0,
    totalItems: 0,
    pageSize: 5,
    selectedReferralId: '',
  }

  componentDidMount(): void {
    if (this.props.selectedPatient && !this.props.selectedPatient.isNew) {
      this.getReferralData()
    }
  }

  componentDidUpdate(prevProps: IReferralsProps): void {
    // Check if patient ID changed
    const patientIdChanged =
      this.props.selectedPatient?.id !== prevProps.selectedPatient?.id &&
      this.props.selectedPatient?.id !== undefined

    // Check if patient just became available (was undefined/null before)
    const patientBecameAvailable =
      !prevProps.selectedPatient?.id &&
      this.props.selectedPatient?.id &&
      !this.props.selectedPatient.isNew

    if (
      (patientIdChanged || patientBecameAvailable) &&
      this.props.selectedPatient &&
      !this.props.selectedPatient.isNew
    ) {
      this.setState(
        {
          page: 1,
          gridData: [],
          selectedReferralId: '',
          pageCount: 0,
          totalItems: 0,
          firstRecordIndex: 0,
          lastRecordIndex: 0,
        },
        () => {
          this.getReferralData()
        }
      )
    }
  }

  public setSelectedReferralRow(referralId: string) {
    this.setState({ selectedReferralId: referralId, gridLoading: true }, async () => {
      try {
        if (this.props.setSelectedReferral) {
          await this.props.setSelectedReferral(referralId)
        }
        if (this.props.setPage) {
          this.props.setPage(1)
        }
        if (this.props.loadEpisodesOfCare) {
          await this.props.loadEpisodesOfCare()
        }
      } catch (error) {
        console.error('Error in setSelectedReferralRow:', error)
      } finally {
        this.setState({ gridLoading: false })
      }
    })
  }
  private getReferralData = async () => {
    if (!this.props.selectedPatient?.id) {
      return
    }

    this.setState({ gridLoading: true })
    const { page, pageSize } = this.state
    var requestData = new SearchRequestBase()
    requestData.page = page
    requestData.pageSize = pageSize

    try {
      const response = await searchReferrals(this.props.selectedPatient.id, requestData)
      const newTotalItems = response.totalItems || 0
      const newPageCount = response.totalPages || 0

      this.setState(
        {
          gridData: response.items || [],
          pageCount: newPageCount,
          totalItems: newTotalItems,
          gridLoading: false,
        },
        () => {
          if (newTotalItems > 0) {
            this.setPaginationOffsetData()
            if (this.state.gridData.length > 0) {
              const firstId = this.state.gridData[0].referralId
              this.setSelectedReferralRow(firstId)
            }
          }
        }
      )
    } catch (error) {
      this.setState({ gridLoading: false })
    }
  }

  public addButtonOnClick = () => {
    if (this.props.selectedPatient && !this.props.selectedPatient.isNew) {
      this.props.editReferral!(undefined, this.props.selectedPatient.id)
    }
  }

  public isAddButtonDisabled = this.props.selectedPatient
    ? this.props.selectedPatient.isNew
    : false

  private setPaginationOffsetData = () => {
    const { page, totalItems, pageSize } = this.state
    const firstRecordIndex = (page - 1) * pageSize + 1
    const lastRecordIndex = page * pageSize < totalItems ? page * pageSize : totalItems
    this.setState({
      firstRecordIndex: firstRecordIndex,
      lastRecordIndex: lastRecordIndex,
      totalItems: totalItems,
    })
  }

  private handlePageChange = (event: any) => {
    const page = event.selected
    this.setState({ page: page + 1 }, () => this.getReferralData())
  }

  private isGridDataEmpty = () => {
    return this.state.totalItems == 0
  }

  private paginationInfo = () => {
    if (!this.isGridDataEmpty()) {
      return (
        <div style={{ paddingBottom: '20px' }} className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {this.state.firstRecordIndex} to {this.state.lastRecordIndex} of{' '}
            {this.state.totalItems} entries
          </Typography>
        </div>
      )
    } else {
      return <></>
    }
  }

  private mapGridData = () => {
    if (this.isGridDataEmpty()) {
      return (
        <TableRow key={1}>
          <TableCell colSpan={14} align="center">
            No records found
          </TableCell>
        </TableRow>
      )
    } else {
      return this.state.gridData.map((referral, index) => (
        <TableRow
          key={index}
          className="gridPadding"
          style={{
            backgroundColor:
              this.state.selectedReferralId == referral.referralId
                ? '#29348F'
                : 'transparent',
            color:
              this.state.selectedReferralId == referral.referralId ? 'white' : 'inherit',
          }}
          onClick={() => this.setSelectedReferralRow(referral.referralId)}
        >
          <TableCell style={{ color: 'inherit' }} align="right">
            {referral.doi != undefined ? moment(referral.doi).format('MM/DD/YYYY') : ''}
          </TableCell>
          <TableCell style={{ color: 'inherit' }} align="right">
            {referral.referralDateDisplay}
          </TableCell>
          <TableCell style={{ color: 'inherit' }}>{referral.insuranceCompany}</TableCell>
          <TableCell style={{ color: 'inherit' }}>{referral.primaryAdjuster}</TableCell>
          <TableCell style={{ color: 'inherit' }}>{referral.ncmCompany}</TableCell>
          <TableCell style={{ color: 'inherit' }}>{referral.primaryNCM}</TableCell>
          <TableCell style={{ color: 'inherit' }}>
            {referral.insuranceClaimNumber}
          </TableCell>
          <TableCell style={{ padding: '0px', color: 'inherit' }}>
            <Edit
              data-testid={`referralGridEdit.${index}`}
              style={{ cursor: 'pointer' }}
              fontSize="small"
              onClick={() =>
                this.props.editReferral!(
                  referral.referralId,
                  this.props.selectedPatient!.id
                )
              }
            />
          </TableCell>
        </TableRow>
      ))
    }
  }

  public render() {
    return (
      <>
        <Grid container direction="row-reverse">
          <Grid item style={{ color: '#AAAAAA' }}>
            <AddCircle
              color={this.isAddButtonDisabled ? 'inherit' : 'primary'}
              onClick={this.isAddButtonDisabled ? undefined : this.addButtonOnClick}
              style={{
                fontSize: '30px',
                marginRight: '8px',
                cursor: this.isAddButtonDisabled ? 'auto' : 'pointer',
              }}
            />
          </Grid>
        </Grid>
        <Grid container item alignItems="center" spacing={2}>
          <Grid item>
            <RecordVoiceOver style={{ fontSize: '32px' }} color="secondary" />
          </Grid>
          <Grid item style={{ fontSize: '20px', paddingTop: '4px' }}>
            Referrals
          </Grid>
        </Grid>
        {this.state.gridLoading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '1200',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <Grid container item md={12} style={{ minWidth: '750px', width: '100%' }}>
          <Table aria-label="simple table" style={{ padding: '20' }}>
            <TableHead>
              <TableRow className="gridPadding">
                <TableCell align="right">DOI</TableCell>
                <TableCell align="right">Referral Date</TableCell>
                <TableCell>Insurance Company</TableCell>
                <TableCell>Primary Adjuster</TableCell>
                <TableCell>NCM Company</TableCell>
                <TableCell>Primary NCM</TableCell>
                <TableCell>Ins Claim Num</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody style={{ width: '100%' }}>{this.mapGridData()}</TableBody>
          </Table>
        </Grid>
        <div className="pagination-row">
          {this.paginationInfo()}
          {this.state.pageCount > 0 && (
            <ReactPaginate
              previousLabel={'<'}
              nextLabel={'>'}
              onPageChange={this.handlePageChange}
              pageCount={this.state.pageCount}
              containerClassName={'pagination'}
              activeClassName={'active'}
              forcePage={this.state.page - 1}
              //@ts-ignore
              renderOnZeroPageCount={null}
            />
          )}
        </div>
      </>
    )
  }
}

const InjectedReferrals = inject<IStores, IReferralsProps, Partial<IReferralsProps>, any>(
  (stores: IStores) => ({
    editReferral: stores.referrals.editReferral,
    loadEpisodesOfCare: stores.patientEdit.getEOCs,
    selectedPatient: stores.patients.selectedPatient,
    setSelectedReferral: stores.referrals.getReferralWithContactsById,
    setPage: stores.patientEdit.setEOCPage,
  })
)(observer(Referrals))

export default InjectedReferrals
